import { argStates } from '@/components/features/collections/arg-states'
import { countries } from '@/components/features/collections/countries'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserRole, UserStatus } from '@/domain/auth/auth'
import { ClientDetail, UpdateClientData, getGenreOptions, getUserRelationshipOptions } from '@/domain/clients/client'
import { updateClientSchema } from '@/domain/clients/client-schemas'
import { IvaCondition, getIvaConditionOptions } from '@/domain/invoices/invoice'
import { DocType, getDocTypeOptions } from '@/domain/shared'
import { useClient, useClients, useUpdateClient } from '@/hooks/api/useClients'
import { useAuthStore } from '@/store/useAuthStore'
import { formatDocNumber } from '@/utils/helpers'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle, Building, Calendar, CreditCard, FileText, Hash, IdCard, Mail, Map, MapPin, Pencil, Phone, Receipt, Save, User, Users, X } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import DateSelector from '../shared/DateSelector'
import { SelectSearch } from '../shared/SelectSearch'

const defaultAddressValues = {
  email: '',
  street: '',
  phone: '+54343',
  city: 'Paraná',
  state: 'Entre Ríos',
  zipCode: '3100',
  country: 'Argentina',
}

const defaultContactValues = {
  name: '',
  dateOfBirth: undefined,
  relationship: '',
  ...defaultAddressValues
}

export const ClientDataForm = ({ clientId, client }: { clientId: string, client: ClientDetail }) => {
  const [editingEntity, setEditingEntity] = useState<string | null>(null)
  const [activeEntityTab, setActiveEntityTab] = useState<string>('titular')
  const [invoiceSameAsHolder, setInvoiceSameAsHolder] = useState<boolean>(true)

  const { hasAnyRole } = useAuthStore()
  const canEdit = hasAnyRole([UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin])

  const updateMutation = useUpdateClient()
  const updateClientSchemaForService = updateClientSchema(activeEntityTab);
  const [searchTerm, setSearchTerm] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      // query will refetch via key change
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const form = useForm<UpdateClientData>({
    resolver: zodResolver(updateClientSchemaForService),
    defaultValues: {
      email: '',
      status: UserStatus.Active,
      userData: {
        legalName: '',
        docType: DocType.DNI,
        docNumber: '',
        observations: '',
        ...defaultAddressValues
      },
      reference1: { ...defaultContactValues },
      reference2: { ...defaultContactValues },
    },
  })

  const { data: clientsResponse } = useClients({
    status: UserStatus.Active,
    search: searchTerm || undefined,
    limit: 500,
  })

  const payerClients = clientsResponse?.data || []
  const selectedPaidByUserId = form.watch('userData.paidByUserId')

  const { data: selectedClientData } = useClient(selectedPaidByUserId || '')
  const payerClientData = selectedClientData?.userData ?? null

  useEffect(() => {
    if (payerClientData && !invoiceSameAsHolder) {
      form.setValue('userData.invoiceLegalName', payerClientData.legalName || '', { shouldValidate: false });
      form.setValue('userData.invoiceDocType', payerClientData.docType || DocType.DNI, { shouldValidate: false });
      form.setValue('userData.invoiceDocNumber', payerClientData.docNumber || '', { shouldValidate: false });
      form.setValue('userData.invoiceAddress', payerClientData.address || '', { shouldValidate: false });
      form.setValue('userData.ivaCondition', payerClientData.ivaCondition || IvaCondition.CONSUMIDOR_FINAL, { shouldValidate: false });
    }
  }, [payerClientData, invoiceSameAsHolder]);

  useEffect(() => {
    if (client) {
      setInvoiceSameAsHolder(!client.userData.paidByUserId);

      form.reset({
        email: client.email || '',
        status: client.status,
        userData: {
          legalName: client.userData?.legalName || '',
          genre: client.userData?.genre || '',
          docType: client.userData?.docType || '',
          docNumber: client.userData?.docNumber || '',
          dateOfBirth: client.userData?.dateOfBirth,
          phone: client.userData?.phone || defaultAddressValues.phone,
          email: client.userData?.email || defaultAddressValues.email,
          street: client.userData?.street || defaultAddressValues.street,
          city: client.userData?.city || defaultAddressValues.city,
          state: client.userData?.state || defaultAddressValues.state,
          zipCode: client.userData?.zipCode || defaultAddressValues.zipCode,
          country: client.userData?.country || defaultAddressValues.country,
          observations: client.userData?.observations || '',
          invoiceLegalName: client.userData?.legalName || '',
          invoiceDocType: client.userData?.docType || DocType.DNI,
          invoiceDocNumber: client.userData?.docNumber || '',
          ivaCondition: client.userData?.ivaCondition || IvaCondition.CONSUMIDOR_FINAL,
          invoiceAddress: client.userData?.address || '',
          paidByUserId: client.userData?.paidByUserId || '',
        },
        reference1: {
          name: client.reference1?.name || defaultContactValues.name,
          genre: client.reference1?.genre || '',
          dateOfBirth: client.reference1?.dateOfBirth,
          relationship: client.reference1?.relationship || defaultContactValues.relationship,
          phone: client.reference1?.phone || defaultContactValues.phone,
          email: client.reference1?.email || defaultContactValues.email,
          street: client.reference1?.street || defaultContactValues.street,
          city: client.reference1?.city || defaultContactValues.city,
          state: client.reference1?.state || defaultContactValues.state,
          zipCode: client.reference1?.zipCode || defaultContactValues.zipCode,
          country: client.reference1?.country || defaultContactValues.country,
          observations: client.reference1?.observations || '',
        },
        reference2: {
          name: client.reference2?.name || defaultContactValues.name,
          genre: client.reference2?.genre || '',
          dateOfBirth: client.reference2?.dateOfBirth,
          relationship: client.reference2?.relationship || defaultContactValues.relationship,
          phone: client.reference2?.phone || defaultContactValues.phone,
          email: client.reference2?.email || defaultContactValues.email,
          street: client.reference2?.street || defaultContactValues.street,
          city: client.reference2?.city || defaultContactValues.city,
          state: client.reference2?.state || defaultContactValues.state,
          zipCode: client.reference2?.zipCode || defaultContactValues.zipCode,
          country: client.reference2?.country || defaultContactValues.country,
          observations: client.reference2?.observations || '',
        },
      })
    }
  }, [client, form])

  const userCountry = form.watch('userData.country') || client?.userData?.country || 'Argentina';
  const ref1Country = form.watch('reference1.country') || client?.reference1?.country || 'Argentina';
  const ref2Country = form.watch('reference2.country') || client?.reference2?.country || 'Argentina';

  const handleEntitySave = async (data: UpdateClientData) => {
    if (!canEdit) return
    const payload: UpdateClientData = {}

    if (editingEntity === 'titular') {
      payload.userData = {
        legalName: data.userData?.legalName,
        genre: data.userData?.genre,
        docType: data.userData?.docType,
        docNumber: data.userData?.docNumber,
        dateOfBirth: data.userData?.dateOfBirth,
        phone: data.userData?.phone,
        email: data.userData?.email,
        street: data.userData?.street,
        city: data.userData?.city,
        state: data.userData?.state,
        zipCode: data.userData?.zipCode,
        country: data.userData?.country,
        observations: data.userData?.observations,
      }
    }
    else if (editingEntity === 'invoice_data') {
      payload.userData = {
        ivaCondition: data.userData?.ivaCondition,
        paidByUserId: data.userData?.paidByUserId,
      }
    } else if (editingEntity === 'ref1') {
      payload.reference1 = {
        name: data.reference1?.name,
        genre: data.reference1?.genre,
        dateOfBirth: data.reference1?.dateOfBirth,
        relationship: data.reference1?.relationship,
        phone: data.reference1?.phone,
        email: data.reference1?.email,
        street: data.reference1?.street,
        city: data.reference1?.city,
        state: data.reference1?.state,
        zipCode: data.reference1?.zipCode,
        country: data.reference1?.country,
        observations: data.reference1?.observations,
      }
    } else if (editingEntity === 'ref2') {
      payload.reference2 = {
        name: data.reference2?.name,
        genre: data.reference2?.genre,
        dateOfBirth: data.reference2?.dateOfBirth,
        relationship: data.reference2?.relationship,
        phone: data.reference2?.phone,
        email: data.reference2?.email,
        street: data.reference2?.street,
        city: data.reference2?.city,
        state: data.reference2?.state,
        zipCode: data.reference2?.zipCode,
        country: data.reference2?.country,
        observations: data.reference2?.observations,
      }
    }
    await updateMutation.mutateAsync({ id: clientId, data: payload })
    setEditingEntity(null)
  }

  const renderField = (
    name: any,
    label: string,
    icon: React.ElementType,
    value: any,
    isEditing: boolean,
    type: string = 'text',
    isSelect: boolean = false,
    selectOptions: ({ label: string; value: string } | string)[] = [],
    className: string = '',
  ) => {
    const Icon = icon;

    const getDisplayValue = () => {
      if (isSelect && value) {
        const option = selectOptions.find(opt =>
          (typeof opt === 'string' ? opt : opt.value) === value
        )
        if (option) {
          return typeof option === 'string' ? option : option.label
        }
      }
      return value || '-';
    }

    const fieldError = typeof name === 'string'
      ? name.split('.').reduce((obj: any, key: string) => obj?.[key], form.formState.errors)
      : undefined;
    const errorMessage = fieldError?.message as string | undefined;

    return (
      <div className={`space-y-2 ${className}`}>
        <Label className={`flex items-center text-muted-foreground mb-1 ${errorMessage ? 'text-destructive font-semibold' : ''}`}>
          <Icon className="w-4 h-4 mr-2" />
          {label}
        </Label>
        {isEditing ? (
          isSelect ? (
            <Controller
              control={form.control}
              name={name}
              render={({ field }) => (
                <Select
                  onValueChange={(v) => { field.onChange(v); form.trigger(name); }}
                  value={field.value}
                  defaultValue={value || undefined}
                >
                  <SelectTrigger className={errorMessage ? 'border-destructive focus:ring-destructive' : ''}>
                    <SelectValue placeholder="Seleccione una opción" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectOptions.map((opt) => {
                      const val = typeof opt === 'string' ? opt : opt.value;
                      const display = typeof opt === 'string' ? opt : opt.label;
                      return (
                        <SelectItem key={val} value={val}>
                          {display}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            />
          ) : (
            type === 'date' ?
              <Controller
                control={form.control}
                name={name}
                render={({ field }) => (
                  <div className={errorMessage ? 'border border-destructive rounded-md' : ''}>
                    <DateSelector initialDate={field.value} onDateSelected={(date: Date) => { field.onChange(date); form.trigger(name); }} />
                  </div>
                )}
              /> :
              <Input
                type={type}
                {...form.register(name)}
                defaultValue={value || ''}
                className={errorMessage ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
          )
        ) : (
          type === 'date' ?
            <p className="text-sm font-medium">
              {value ? new Date(value).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
            </p> :
            <p className="text-sm font-medium">
              {getDisplayValue()}
            </p>
        )}
        {isEditing && errorMessage && (
          <p className="text-[0.8rem] font-medium text-destructive mt-1">
            {errorMessage}
          </p>
        )}
      </div>
    );
  };

  return (
    <Card>
      <Tabs value={activeEntityTab} onValueChange={(val) => {
        if (editingEntity) return;
        // if (!canEdit) return;
        setActiveEntityTab(val);
      }}>
        <div className="border-b px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="titular" disabled={editingEntity !== null && editingEntity !== 'titular'}>
              <User className="w-4 h-4 mr-2" />
              Titular
            </TabsTrigger>
            <TabsTrigger value="invoice_data" disabled={editingEntity !== null && editingEntity !== 'invoice_data'}>
              <CreditCard className="w-4 h-4 mr-2" />
              Facturación
            </TabsTrigger>
            <TabsTrigger value="ref1" disabled={editingEntity !== null && editingEntity !== 'ref1'}>
              <Users className="w-4 h-4 mr-2" />
              Contacto 1
            </TabsTrigger>
            <TabsTrigger value="ref2" disabled={editingEntity !== null && editingEntity !== 'ref2'}>
              <Users className="w-4 h-4 mr-2" />
              Contacto 2
            </TabsTrigger>
          </TabsList>

          {canEdit && !editingEntity ? (
            <Button variant="outline" size="sm" onClick={() => setEditingEntity(activeEntityTab)}>
              <Pencil className="mr-2 h-4 w-4" />
              Editar {activeEntityTab === 'titular' ? 'Titular'
                : activeEntityTab === 'invoice_data' ? 'Facturación'
                  : activeEntityTab === 'ref1' ? 'Contacto 1' : 'Contacto 2'}
            </Button>
          ) : canEdit && editingEntity === activeEntityTab ? (
            <div className="flex space-x-2 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingEntity(null)
                  form.reset()
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={form.handleSubmit(handleEntitySave)}
                disabled={updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          ) : null}
        </div>

        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={canEdit ? form.handleSubmit(handleEntitySave) : undefined}>
              <TabsContent value="titular" className="m-0 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {renderField('userData.legalName', 'Apellido, Nombre', User, client.userData?.legalName, editingEntity === 'titular')}
                  {renderField('userData.genre', 'Género', User, client.userData?.genre, editingEntity === 'titular', 'select', true, getGenreOptions())}
                  {renderField('userData.docType', 'Tipo Documento', IdCard, client.userData?.docType, editingEntity === 'titular', 'select', true, getDocTypeOptions())}
                  {renderField('userData.docNumber', 'Número Documento', CreditCard, formatDocNumber(client.userData?.docNumber || '', client.userData?.docType || ''), editingEntity === 'titular')}
                  {renderField('userData.dateOfBirth', 'Fecha de Nacimiento', Calendar, client.userData?.dateOfBirth, editingEntity === 'titular', 'date')}
                  {renderField('userData.email', 'Email', Mail, client.userData?.email, editingEntity === 'titular', 'email')}
                  {renderField('userData.phone', 'Teléfono', Phone, client.userData?.phone, editingEntity === 'titular')}
                  {renderField('userData.street', 'Dirección', MapPin, client.userData?.street, editingEntity === 'titular')}
                  {renderField('userData.city', 'Ciudad', Building, client.userData?.city, editingEntity === 'titular')}
                  {renderField('userData.zipCode', 'Código Postal', Hash, client.userData?.zipCode, editingEntity === 'titular')}
                  {renderField('userData.country', 'País', Map, client.userData?.country, editingEntity === 'titular', 'text', true, countries)}
                  {renderField('userData.state', 'Provincia/Estado', Map, client.userData?.state, editingEntity === 'titular', 'text', userCountry === 'Argentina', userCountry === 'Argentina' ? argStates : [])}
                  {renderField('userData.observations', 'Observaciones', FileText, client.userData?.observations, editingEntity === 'titular', 'text', false, [], "col-span-2")}
                </div>
              </TabsContent>

              <TabsContent value="invoice_data" className="m-0 space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {renderField(
                      'userData.invoiceLegalName',
                      'Nombre Legal / Empresa',
                      User,
                      !invoiceSameAsHolder && payerClientData
                        ? (payerClientData.invoiceLegalName || payerClientData.legalName || '' + ` (${(' (' + payerClientData.code + ')')})`)
                        : form.watch('userData.invoiceLegalName'),
                      false,
                      'text',
                      false,
                      [],
                      !invoiceSameAsHolder ? '' : 'opacity-50 pointer-events-none'
                    )}
                    {renderField(
                      'userData.invoiceDocType',
                      'Tipo Documento',
                      IdCard,
                      !invoiceSameAsHolder && payerClientData
                        ? payerClientData.invoiceDocType || ''
                        : form.watch('userData.invoiceDocType'),
                      false,
                      'select',
                      true,
                      getDocTypeOptions(),
                      !invoiceSameAsHolder ? '' : 'opacity-50 pointer-events-none'
                    )}
                    {renderField(
                      'userData.invoiceDocNumber',
                      'Número Documento',
                      CreditCard,
                      !invoiceSameAsHolder && payerClientData
                        ? formatDocNumber(payerClientData.invoiceDocNumber || payerClientData.docNumber || '', payerClientData.invoiceDocType)
                        : formatDocNumber(form.watch('userData.invoiceDocNumber') || '', form.watch('userData.invoiceDocType')),
                      false,
                      'text',
                      false,
                      [],
                      !invoiceSameAsHolder ? '' : 'opacity-50 pointer-events-none'
                    )}
                    {renderField(
                      'userData.invoiceAddress',
                      'Domicilio de facturación',
                      MapPin,
                      !invoiceSameAsHolder && payerClientData
                        ? payerClientData.invoiceAddress || payerClientData.street || ''
                        : form.watch('userData.invoiceAddress'),
                      false,
                      'text',
                      false,
                      [],
                      'col-span-2 ' + (!invoiceSameAsHolder ? '' : 'opacity-50 pointer-events-none')
                    )}
                    {renderField(
                      'userData.ivaCondition',
                      'Condición IVA',
                      Receipt,
                      form.watch('userData.ivaCondition'),
                      editingEntity === 'invoice_data' && invoiceSameAsHolder,
                      'select',
                      true,
                      getIvaConditionOptions()
                    )}

                    {!editingEntity && !invoiceSameAsHolder &&
                      <Alert variant={"info"} className='col-span-3'>
                        <AlertDescription className='flex items-center gap-2'>
                          <AlertCircle className='w-4 flex-shrink-0' />
                          Los datos de facturación se encuentran asociados a otro cliente {payerClientData ? `N° ${payerClientData.code} - ${payerClientData.legalName}` : ''}
                        </AlertDescription>
                      </Alert>
                    }

                    {/* Paid By User ID - Searchable Client Selector */}
                    {editingEntity === 'invoice_data' && (
                      <div className="col-span-full space-y-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="invoiceSameAsHolder"
                            checked={invoiceSameAsHolder}
                            onCheckedChange={(checked) => {
                              if (!canEdit) return;
                              setInvoiceSameAsHolder(!!checked);
                              if (client?.userData) {
                                form.setValue('userData.invoiceLegalName', client.userData.legalName || '', { shouldValidate: true });
                                form.setValue('userData.invoiceDocType', client.userData.docType || DocType.DNI, { shouldValidate: true });
                                form.setValue('userData.invoiceDocNumber', client.userData.docNumber || '', { shouldValidate: true });
                                form.setValue('userData.invoiceAddress', (client.userData.street || '') + ' ' + (client.userData.city || '') + ', ' + (client.userData.state || '') + ' (' + (client.userData.zipCode || '') + ')', { shouldValidate: true });
                                form.setValue('userData.ivaCondition', IvaCondition.CONSUMIDOR_FINAL, { shouldValidate: true });
                                form.setValue('userData.paidByUserId', '', { shouldValidate: true });
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            disabled={!canEdit}
                          />
                          <Label htmlFor="invoiceSameAsHolder" className="text-sm">
                            Datos de facturación iguales al titular
                          </Label>
                        </div>

                        {!invoiceSameAsHolder && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border rounded-md bg-muted/20">
                            <FormField
                              control={form.control}
                              name="userData.paidByUserId"
                              render={({ field }) => {
                                const selectedClient = selectedClientData?.userData
                                  ? [{
                                    id: selectedClientData.id,
                                    label: `${selectedClientData.userData.legalName || selectedClientData.fullName} - N° ${selectedClientData.userData.code}`,
                                  }]
                                  : [];
                                const searchResults = (payerClients || [])
                                  .filter((clientItem) => !!clientItem.id && !selectedClient.some(i => i.id === clientItem.id))
                                  .map((clientItem) => ({
                                    id: clientItem.id,
                                    label: `${clientItem.name} - N° ${clientItem.code}`
                                  }));

                                return (
                                  <FormItem className="md:col-span-2">
                                    <FormLabel>Datos de facturación de otro cliente</FormLabel>
                                    <SelectSearch
                                      value={field.value!}
                                      onValueChange={(value) => {
                                        if (!canEdit) return;
                                        field.onChange(value);
                                      }}
                                      onSearchChange={(search) => {
                                        if (!canEdit) return;
                                        handleSearchChange(search);
                                      }}
                                      placeholder="Seleccionar cliente"
                                      searchPlaceholder="Buscar cliente..."
                                      emptyMessage="No se encontraron clientes."
                                      items={[...selectedClient, ...searchResults]}
                                      disabled={!canEdit}
                                    />
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="ref1" className="m-0 space-y-6">
                {!client.reference1?.name && editingEntity !== 'ref1' ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Sin datos</AlertTitle>
                    <AlertDescription>No hay información registrada para el Contacto 1.</AlertDescription>
                  </Alert>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {renderField('reference1.name', 'Apellido, Nombre', User, client.reference1?.name, editingEntity === 'ref1')}
                    {renderField('reference1.genre', 'Género', User, client.reference1?.genre, editingEntity === 'ref1', 'select', true, getGenreOptions())}
                    {renderField('reference1.relationship', 'Relación', Users, client.reference1?.relationship, editingEntity === 'ref1', 'select', true, getUserRelationshipOptions())}
                    {renderField('reference1.phone', 'Teléfono', Phone, client.reference1?.phone, editingEntity === 'ref1')}
                    {renderField('reference1.email', 'Email', Mail, client.reference1?.email, editingEntity === 'ref1', 'email')}
                    {renderField('reference1.dateOfBirth', 'Fecha de Nacimiento', Calendar, client.reference1?.dateOfBirth, editingEntity === 'ref1', 'date')}
                    {renderField('reference1.street', 'Dirección', MapPin, client.reference1?.street, editingEntity === 'ref1')}
                    {renderField('reference1.city', 'Ciudad', Building, client.reference1?.city, editingEntity === 'ref1')}
                    {renderField('reference1.zipCode', 'Código Postal', Hash, client.reference1?.zipCode, editingEntity === 'ref1')}
                    {renderField('reference1.state', 'Provincia/Estado', Map, client.reference1?.state, editingEntity === 'ref1', 'text', ref1Country === 'Argentina', ref1Country === 'Argentina' ? argStates : [])}
                    {renderField('reference1.country', 'País', Map, client.reference1?.country, editingEntity === 'ref1', 'text', true, countries)}
                    {renderField('reference1.observations', 'Observaciones', FileText, client.reference1?.observations, editingEntity === 'ref1', 'text')}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="ref2" className="m-0 space-y-6">
                {!client.reference2?.name && editingEntity !== 'ref2' ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Sin datos</AlertTitle>
                    <AlertDescription>No hay información registrada para el Contacto 2.</AlertDescription>
                  </Alert>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {renderField('reference2.name', 'Apellido, Nombre', User, client.reference2?.name, editingEntity === 'ref2')}
                    {renderField('reference2.genre', 'Género', User, client.reference2?.genre, editingEntity === 'ref2', 'select', true, getGenreOptions())}
                    {renderField('reference2.relationship', 'Relación', Users, client.reference2?.relationship, editingEntity === 'ref2', 'select', true, getUserRelationshipOptions())}
                    {renderField('reference2.phone', 'Teléfono', Phone, client.reference2?.phone, editingEntity === 'ref2')}
                    {renderField('reference2.email', 'Email', Mail, client.reference2?.email, editingEntity === 'ref2', 'email')}
                    {renderField('reference2.dateOfBirth', 'Fecha de Nacimiento', Calendar, client.reference2?.dateOfBirth, editingEntity === 'ref2', 'date')}
                    {renderField('reference2.street', 'Dirección', MapPin, client.reference2?.street, editingEntity === 'ref2')}
                    {renderField('reference2.city', 'Ciudad', Building, client.reference2?.city, editingEntity === 'ref2')}
                    {renderField('reference2.zipCode', 'Código Postal', Hash, client.reference2?.zipCode, editingEntity === 'ref2')}
                    {renderField('reference2.state', 'Provincia/Estado', Map, client.reference2?.state, editingEntity === 'ref2', 'text', ref2Country === 'Argentina', ref2Country === 'Argentina' ? argStates : [])}
                    {renderField('reference2.country', 'País', Map, client.reference2?.country, editingEntity === 'ref2', 'text', true, countries)}
                    {renderField('reference2.observations', 'Observaciones', FileText, client.reference2?.observations, editingEntity === 'ref2', 'text')}
                  </div>
                )}
              </TabsContent>
            </form>
          </Form>
        </CardContent>
      </Tabs>
    </Card>
  )
}
